import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCamera,
  faCheckCircle,
  faIdCard,
  faImage,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Modal, Button } from "../ui";
import { useAuth } from "../../context/authContext";
import { userService } from "../../services/api";
import {
  prepareIdentityImage,
  IdentityImageError,
} from "./prepareIdentityImage";
import styles from "./IdentityVerificationModal.module.css";

/** GL-USR-009: la identidad ya estaba verificada → se trata como éxito. */
const ALREADY_VERIFIED_CODE = "GL-USR-009";

const SLOTS = [
  { key: "front", label: "Cédula — frente" },
  { key: "back", label: "Cédula — reverso" },
];

const emptySlot = () => ({ file: null, preview: "", processing: false, error: "" });

function isTouchDevice() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(pointer: coarse)").matches;
}

function UploadSlot({ label, slot, disabled, onSelect, onRemove, autoFocus }) {
  const reactId = useId();
  const inputId = `identity-${reactId}`;
  const hintId = `${inputId}-hint`;
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const showCamera = isTouchDevice();

  const handleInput = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) onSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) onSelect(file);
  };

  const openGallery = () => {
    if (!disabled) galleryRef.current?.click();
  };

  return (
    <div className={styles.slot}>
      <span className={styles.slotLabel} id={`${inputId}-label`}>
        {label}
      </span>

      {slot.preview ? (
        <div className={styles.previewBox}>
          <img
            src={slot.preview}
            alt={`Vista previa: ${label}`}
            className={styles.previewImg}
          />
          <div className={styles.previewActions}>
            <button
              type="button"
              className={styles.slotAction}
              onClick={openGallery}
              disabled={disabled || slot.processing}
              aria-label={`Cambiar foto: ${label}`}
            >
              <FontAwesomeIcon icon={faImage} /> Cambiar
            </button>
            <button
              type="button"
              className={`${styles.slotAction} ${styles.slotActionDanger}`}
              onClick={onRemove}
              disabled={disabled || slot.processing}
              aria-label={`Quitar foto: ${label}`}
            >
              <FontAwesomeIcon icon={faTrash} /> Quitar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ""}`}
          onClick={openGallery}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          disabled={disabled || slot.processing}
          aria-labelledby={`${inputId}-label`}
          aria-describedby={hintId}
          autoFocus={autoFocus}
        >
          <FontAwesomeIcon icon={faIdCard} className={styles.dropIcon} />
          <span className={styles.dropText}>
            {slot.processing ? "Procesando foto…" : "Toca o arrastra la foto aquí"}
          </span>
          <span className={styles.dropHint} id={hintId}>
            JPG o PNG · máx. 5 MB
          </span>
        </button>
      )}

      {showCamera && !slot.processing ? (
        <button
          type="button"
          className={styles.cameraBtn}
          onClick={() => !disabled && cameraRef.current?.click()}
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faCamera} /> Tomar foto
        </button>
      ) : null}

      <input
        ref={galleryRef}
        id={inputId}
        type="file"
        accept="image/*"
        className={styles.hiddenInput}
        onChange={handleInput}
        tabIndex={-1}
        aria-hidden="true"
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className={styles.hiddenInput}
        onChange={handleInput}
        tabIndex={-1}
        aria-hidden="true"
      />

      {slot.error ? (
        <p className={styles.slotError} role="alert">
          {slot.error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Verificación de identidad en la web: fotos de la cédula (frente y reverso)
 * → POST /users/{id}/verify_identity/ → refresca el perfil (is_identity_verified).
 */
export default function IdentityVerificationModal({ isOpen, onClose, onVerified }) {
  const { user, profile, loadProfile } = useAuth();
  const userId = user?.id || profile?.id;

  const [slots, setSlots] = useState({ front: emptySlot(), back: emptySlot() });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const previewsRef = useRef([]);
  const returnFocusRef = useRef(null);

  const revokeAll = useCallback(() => {
    previewsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewsRef.current = [];
  }, []);

  const wasOpenRef = useRef(false);

  // Al abrir: recordar el foco para devolverlo al cerrar. Al cerrar: limpiar estado y
  // previews (las fotos se conservan mientras el modal esté abierto, incluso tras un error).
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      returnFocusRef.current = document.activeElement;
      return undefined;
    }
    if (!wasOpenRef.current) return undefined;
    wasOpenRef.current = false;
    revokeAll();
    setSlots({ front: emptySlot(), back: emptySlot() });
    setStatus("idle");
    setErrorMessage("");
    const el = returnFocusRef.current;
    if (el && typeof el.focus === "function" && document.contains(el)) {
      el.focus();
    }
    returnFocusRef.current = null;
    return undefined;
  }, [isOpen, revokeAll]);

  useEffect(() => () => revokeAll(), [revokeAll]);

  const submitting = status === "submitting";

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const updateSlot = (key, patch) =>
    setSlots((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const handleSelect = async (key, rawFile) => {
    if (status === "error") {
      setStatus("idle");
      setErrorMessage("");
    }
    updateSlot(key, { processing: true, error: "" });
    try {
      const file = await prepareIdentityImage(rawFile);
      const preview = URL.createObjectURL(file);
      previewsRef.current.push(preview);
      setSlots((prev) => {
        const old = prev[key].preview;
        if (old) {
          URL.revokeObjectURL(old);
          previewsRef.current = previewsRef.current.filter((u) => u !== old);
        }
        return { ...prev, [key]: { file, preview, processing: false, error: "" } };
      });
    } catch (err) {
      updateSlot(key, {
        processing: false,
        error:
          err instanceof IdentityImageError
            ? err.message
            : "No pudimos leer la foto. Intenta con otra imagen.",
      });
    }
  };

  const handleRemove = (key) => {
    setSlots((prev) => {
      const old = prev[key].preview;
      if (old) {
        URL.revokeObjectURL(old);
        previewsRef.current = previewsRef.current.filter((u) => u !== old);
      }
      return { ...prev, [key]: emptySlot() };
    });
  };

  const canSubmit =
    Boolean(slots.front.file && slots.back.file) &&
    !slots.front.processing &&
    !slots.back.processing &&
    !submitting &&
    Boolean(userId);

  const markVerified = async () => {
    setStatus("success");
    try {
      await loadProfile?.();
    } catch {
      /* el perfil se re-sincroniza al volver a la pestaña */
    }
    onVerified?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setStatus("submitting");
    setErrorMessage("");
    try {
      const result = await userService.verifyIdentity(
        userId,
        slots.front.file,
        slots.back.file
      );
      if (result.ok || result.code === ALREADY_VERIFIED_CODE) {
        await markVerified();
        return;
      }
      setErrorMessage(result.message || "No fue posible verificar tu identidad.");
      setStatus("error");
    } catch {
      setErrorMessage("Error de conexión. Revisa tu internet e intenta de nuevo.");
      setStatus("error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={status === "success" ? "Verificación completa" : "Verifica tu identidad"}
      size="md"
    >
      {status === "success" ? (
        <div className={styles.successBox} role="status" aria-live="polite">
          <FontAwesomeIcon icon={faCheckCircle} className={styles.successIcon} />
          <h3 className={styles.successTitle}>¡Identidad verificada!</h3>
          <p className={styles.successText}>
            Tu cédula fue verificada correctamente. Ya puedes seguir apostando y
            solicitar retiros de tus premios.
          </p>
          <Button onClick={onClose} autoFocus>
            Listo
          </Button>
        </div>
      ) : (
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <p className={styles.intro}>
            Sube una foto de cada lado de tu cédula de ciudadanía. Revisaremos que
            el número coincida con el de tu cuenta.
          </p>

          <ul className={styles.tips}>
            <li>Usa buena luz y evita reflejos o brillos sobre el documento.</li>
            <li>Que se vea el documento completo, sin dedos ni bordes cortados.</li>
            <li>Usa la cédula original, no una fotocopia ni una foto de pantalla.</li>
          </ul>

          <div className={styles.slots}>
            {SLOTS.map((s, idx) => (
              <UploadSlot
                key={s.key}
                label={s.label}
                slot={slots[s.key]}
                disabled={submitting}
                onSelect={(file) => handleSelect(s.key, file)}
                onRemove={() => handleRemove(s.key)}
                autoFocus={idx === 0}
              />
            ))}
          </div>

          {status === "error" && errorMessage ? (
            <div className={styles.errorBox} role="alert">
              <p className={styles.errorTitle}>No pudimos verificar tu identidad</p>
              <p className={styles.errorText}>{errorMessage}</p>
            </div>
          ) : null}

          {submitting ? (
            <p className={styles.progress} role="status" aria-live="polite">
              <span className={styles.progressSpinner} aria-hidden />
              Verificando tu cédula… esto puede tardar unos segundos.
            </p>
          ) : null}

          <div className={styles.actions}>
            <Button variant="ghost" onClick={handleClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit} loading={submitting}>
              {status === "error" ? "Intentar de nuevo" : "Verificar mi identidad"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
